import {lazy, type ComponentType, type LazyExoticComponent} from 'react'
import * as Sentry from '@sentry/react'

const RETRY_FLAG = 'barbermate-chunk-retry'

// After a deploy, old chunk hashes 404 and the SPA fallback returns
// index.html (text/html). The browser refuses the script and the lazy
// import rejects — leaving Suspense stuck and the user on a blank page.
// Reload once to pick up the new HTML + chunk hashes; if the same import
// fails again after the reload we let the error propagate so the
// boundary + Sentry can surface a real problem.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
    importer: () => Promise<{default: T}>,
): LazyExoticComponent<T> {
    return lazy(async () => {
        try {
            return await importer()
        } catch (error) {
            const alreadyReloaded = window.sessionStorage.getItem(RETRY_FLAG) === '1'
            if (alreadyReloaded) throw error

            window.sessionStorage.setItem(RETRY_FLAG, '1')

            // Wrap so the message bypasses the chunk-error ignore list in
            // main.tsx — we want this specific recovery path visible.
            const original = error instanceof Error ? error.message : String(error)
            Sentry.captureException(
                new Error(`Stale chunk recovery: ${original}`),
                {
                    tags: {stale_chunk_recovery: 'true'},
                    level: 'warning',
                    extra: {originalError: error},
                },
            )

            window.location.reload()
            // Keep Suspense pending until the reload tears the page down.
            return new Promise<{default: T}>(() => {})
        }
    })
}

export function clearChunkRetryFlag(): void {
    window.sessionStorage.removeItem(RETRY_FLAG)
}
